import React, { useEffect, useState } from 'react';
import GmModal from 'app/shared/modal/modal';
import { Button } from 'app/shared/button';
import { useAdminService } from 'hooks/api/users/admin';
import { AddAdminForm } from './create';
import styles from './admins.module.css';

const adminName = (admin) =>
  admin.name ||
  [admin.first_name || admin.firstname, admin.last_name || admin.lastname]
    .filter(Boolean)
    .join(' ') ||
  admin.email ||
  '—';

const adminInitials = (admin) => {
  const source = adminName(admin);
  if (source && source !== '—') {
    return source
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('');
  }
  return (admin.email || '?')[0].toUpperCase();
};

const AdminsSettings = () => {
  const { fetchAdmins, deleteAdmin } = useAdminService();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [show_add, setShowAdd] = useState(false);
  const [admin_to_remove, setAdminToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchAdmins({ query_string: 'population=500' })
      .then(({ admins: list }) => setAdmins(list || []))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreated = (admin) => setAdmins((prev) => [admin, ...prev]);

  const handleRemove = async () => {
    const id = admin_to_remove.id || admin_to_remove._id;
    setRemoving(true);
    const ok = await deleteAdmin(id);
    setRemoving(false);
    if (ok) {
      setAdmins((prev) => prev.filter((a) => (a.id || a._id) !== id));
      setAdminToRemove(null);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.heading}>Admins</h2>
          <p className={styles.subtle}>People with access to the admin dashboard.</p>
        </div>
        <Button text="+ Add admin" onClick={() => setShowAdd(true)} />
      </div>

      {loading && <p className={styles.empty}>Loading…</p>}
      {!loading && admins.length === 0 && <p className={styles.empty}>No admins yet.</p>}

      {!loading && admins.length > 0 && (
        <div className={styles.list}>
          {admins.map((admin) => (
            <div key={admin.id || admin._id || admin.email} className={styles.row}>
              <div className={styles.identity}>
                <span className={styles.avatar}>{adminInitials(admin)}</span>
                <div className={styles.identityText}>
                  <span className={styles.name}>{adminName(admin)}</span>
                  <span className={styles.email}>{admin.email}</span>
                </div>
              </div>
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => setAdminToRemove(admin)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <GmModal
        show_header={false}
        show_modal={show_add}
        bodyClassName={styles.addModal}
        onClose={() => setShowAdd(false)}
      >
        <AddAdminForm closeModal={() => setShowAdd(false)} onCreated={handleCreated} />
      </GmModal>

      <GmModal
        show_header={false}
        show_modal={!!admin_to_remove}
        bodyClassName={styles.confirmModal}
        onClose={() => setAdminToRemove(null)}
      >
        <div className={styles.confirm}>
          <h2 className={styles.title}>Remove admin</h2>
          <p className={styles.confirmText}>
            Remove <strong>{admin_to_remove ? adminName(admin_to_remove) : ''}</strong>? They will
            lose access to the dashboard.
          </p>
          <div className={styles.actions}>
            <Button text="Cancel" type="secondary" onClick={() => setAdminToRemove(null)} />
            <Button text="Remove" onClick={handleRemove} loading={removing} />
          </div>
        </div>
      </GmModal>
    </div>
  );
};

export default AdminsSettings;
